'use client'

import React, { ReactNode, useEffect, useRef, useState } from 'react'
import styles from './CommonComponents.module.scss'

interface FlipCardComponentProps {
  items: ReactNode[]
}

const FlipCardComponent = ({ items }: FlipCardComponentProps) => {
  const [currentIndex, setCurrentIndex] = useState(0)
  const [isAnimating, setIsAnimating] = useState(false)
  const intervalRef = useRef<NodeJS.Timeout | null>(null)
  const animationTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  useEffect(() => {
    if (items.length <= 1) return

    // Clear any existing intervals/timeouts
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }
    if (animationTimeoutRef.current) {
      clearTimeout(animationTimeoutRef.current)
      animationTimeoutRef.current = null
    }

    // Reset cycle count
    setCurrentIndex(0)
    setIsAnimating(false)

    // Start the animation cycle after initial 2 second delay
    const initialTimeout = setTimeout(() => {
      const animate = () => {
        // Start animation
        setIsAnimating(true)

        // After animation completes, update index
        animationTimeoutRef.current = setTimeout(() => {
          setCurrentIndex(prev => {
            const nextIndex = (prev + 1) % items.length
            return nextIndex
          })
          setIsAnimating(false)
        }, 1000) // Animation duration
      }

      // Start interval for animation
      intervalRef.current = setInterval(animate, 2500) // 2.5 second interval
    }, 1000) // Initial 1 second delay

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
      if (animationTimeoutRef.current) {
        clearTimeout(animationTimeoutRef.current)
        animationTimeoutRef.current = null
      }
      clearTimeout(initialTimeout)
    }
  }, [items.length])

  if (items.length === 0) return null
  if (items.length === 1) {
    return items?.[0]
  }

  const currentItem = items[currentIndex]
  const nextItem = items[(currentIndex + 1) % items.length]

  return (
    <div className={styles.animatedItemContainer}>
      <div key={`current-${currentIndex}`} className={`${styles.animatedItem} ${isAnimating ? styles.slideUp : ''}`}>
        {currentItem}
      </div>
      {isAnimating && (
        <div key={`next-${(currentIndex + 1) % items.length}`} className={`${styles.animatedItem} ${styles.slideIn}`}>
          {nextItem}
        </div>
      )}
    </div>
  )
}

export default FlipCardComponent
